import type { LDClient, LDFlagChangeset, LDFlagSet, LDSingleKindContext } from 'launchdarkly-js-client-sdk';
import { initialize } from 'launchdarkly-js-client-sdk';
import type { FC, PropsWithChildren } from 'react';
import { createContext, useContext, useEffect, useState } from 'react';
import { FFDefaults } from '~/constants';
import { Logger } from '~/lib/logger';
import { getFlattenedFlagsFromChangeset } from '~/utils';

const logger = new Logger('LaunchDarklyProvider');

type LaunchDarklyContextProps = {
  loading: boolean;
  flags: LDFlagSet;
  ldClientEnvID?: string;
  ldClient?: LDClient;
  /**
   * The Launch Darkly context 'kind'
   * @see https://docs.launchdarkly.com/home/observability/context-kinds
   */
  context: LDSingleKindContext;
};

const LaunchDarklyContext = createContext<LaunchDarklyContextProps>({
  loading: true,
  flags: {},
  ldClientEnvID: import.meta.env?.VITE_LAUNCH_DARKLY_CLIENT_ENV_ID as string | undefined,
  ldClient: undefined,
  context: {
    kind: 'user',
    key: 'shared-anonymous',
    anonymous: true
  }
});

export const LaunchDarklyProvider: FC<PropsWithChildren<LaunchDarklyContextProps>> = ({
  children,
  context,
  flags: initialFlags,
  ldClientEnvID
}) => {
  const [loading, setLoading] = useState(true);
  const [flags, setFlags] = useState<LaunchDarklyContextProps['flags']>(initialFlags);
  const [ldClient, setLdClient] = useState<LDClient | undefined>(undefined);

  // Initialize the LD client
  useEffect(() => {
    const listenForFlagChanges = async () => {
      if (!ldClientEnvID) {
        throw new Error('LaunchDarklyProvider requires a ldClientEnvID prop');
      }
      const client = initialize(ldClientEnvID, context);
      setLdClient(client);

      try {
        client.on('change', (changes: LDFlagChangeset) => {
          // We filter out all the flags not included in FFDefaults as they are not relevant to this hcp version
          const flattened: LDFlagSet = getFlattenedFlagsFromChangeset(changes, FFDefaults);
          if (Object.keys(flattened).length) {
            setFlags((prevFlags) => ({ ...prevFlags, ...flattened }));
          }

          setLoading(false);
        });
      } catch (error) {
        logger.error('Error initializing LD client', error);
        setLoading(false);
      }
    };
    listenForFlagChanges();
  }, [context, flags, ldClientEnvID]);

  return (
    <LaunchDarklyContext.Provider value={{ loading, flags, ldClient, context }}>
      {children}
    </LaunchDarklyContext.Provider>
  );
};

export const useLaunchDarklyContext = () => {
  const context = useContext(LaunchDarklyContext);
  if (!context) {
    throw new Error('useLaunchDarklyContext must be used within a LaunchDarklyProvider');
  }

  const flagsWithDefaults = {
    ...FFDefaults,
    ...context.flags
  };

  return {
    ...context,
    flags: flagsWithDefaults
  };
};

export const LaunchDarklyProviderWrapper: FC<PropsWithChildren> = ({ children }) => {
  const { ldClientEnvID, flags, context, loading } = useLaunchDarklyContext();

  return (
    <LaunchDarklyProvider context={context} flags={flags} ldClientEnvID={ldClientEnvID} loading={loading}>
      {children}
    </LaunchDarklyProvider>
  );
};
